import i18n, { Resource } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { Settings as LuxonSettings } from "luxon";
import { initReactI18next } from "react-i18next";

// Discover every locale at build time
// src/locales/<lng>/translation.json  ->  { <lng>: { translation: {...} } }
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "../locales/*/translation.json",
  { eager: true },
);

const resources: Resource = {};
for (const [path, module] of Object.entries(localeModules)) {
  const language = path.match(/\/locales\/([^/]+)\//)?.[1];
  if (language) {
    resources[language] = { translation: module.default };
  }
}

// Optional per-language config
// src/locales/<lng>/config.json  ->  { hidden?, label?, flag? }
export type LanguageConfig = {
  hidden?: boolean;
  label?: string;
  flag?: string;
};

const configModules = import.meta.glob<{ default: LanguageConfig }>(
  "../locales/*/config.json",
  { eager: true },
);

const LANGUAGE_CONFIG: Record<string, LanguageConfig> = {};
for (const [path, module] of Object.entries(configModules)) {
  const language = path.match(/\/locales\/([^/]+)\//)?.[1];
  if (language) {
    LANGUAGE_CONFIG[language] = module.default;
  }
}

/** Every language that has a translation.json (hidden or not). */
export const SUPPORTED_LANGUAGES = Object.keys(resources).sort();
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

const endonym = (language: string): string => {
  try {
    const name = new Intl.DisplayNames([language], { type: "language" }).of(
      language,
    );

    if (name) {
      return name.charAt(0).toLocaleUpperCase(language) + name.slice(1);
    }
  } catch {
    console.warn(`Failed to get name for language "${language}"`);
  }

  return language;
};

/** Languages offered in the switcher - excludes any with `hidden: true`. */
export const VISIBLE_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (language) => !LANGUAGE_CONFIG[language]?.hidden,
);

/** Display name per language: config `label` override, else the endonym. */
export const LANGUAGE_LABELS: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((language) => [
    language,
    LANGUAGE_CONFIG[language]?.label ?? endonym(language),
  ]),
);

/** Optional flag emoji / icon per language (from config.json). */
export const LANGUAGE_FLAGS: Record<string, string | undefined> =
  Object.fromEntries(
    SUPPORTED_LANGUAGES.map((language) => [
      language,
      LANGUAGE_CONFIG[language]?.flag,
    ]),
  );

export const isSupportedLanguage = (
  value: string | undefined | null,
): value is SupportedLanguage => !!value && SUPPORTED_LANGUAGES.includes(value);

// Apply the active language to surfaces outside React's string rendering
// e.g. `<html lang>` and Luxon's default locale.
const applyLocaleSideEffects = () => {
  const language = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE;
  LuxonSettings.defaultLocale = language;
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
};

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES,
      load: "languageOnly",
      nonExplicitSupportedLngs: true,
      returnNull: false,
      interpolation: {
        escapeValue: false,
      },
      // The browser language is only the pre-auth default.
      // A signed-in user's preference is the source of truth and is applied by <LanguageSync> once known.
      // Priority: user preference > browser language.
      detection: {
        order: ["navigator"],
        caches: [],
      },
    });

  applyLocaleSideEffects();
  i18n.on("languageChanged", applyLocaleSideEffects);
}

export default i18n;
